export type ReportProject = {
  projectId: number;
  navn: string;
  status: string | null;
  harOkonomi: boolean;
  eierandel: number | null;
  budsjettVersjon: string | null;
  budInnt: number;
  budKost: number;
  resultat: number;
  margin: number;
  gbAndel: number | null;
  kostAkk: number;
  kostKvartal: number;
  forbrukPct: number | null;
  salg: {
    total: number;
    solgtAkk: number;
    solgtKvartal: number;
    salgsgrad: number | null;
    verdiAkk: number;
    verdiKvartal: number;
  };
  fremdrift: {
    milepaelerNadd: number;
    milepaelerTotal: number;
    milepaelerNaddKvartal: number;
    milepaelerForsinket: number;
    aktiviteterFullfortKvartal: number;
    fullfortPct: number | null;
  };
};

export type ReportData = {
  quarter: {
    year: number;
    q: number;
    label: string;
    from: string;
    toExclusive: string;
  };
  portfolio: {
    prosjekterTotalt: number;
    prosjekterMedOkonomi: number;
    statusFordeling: Record<string, number>;
    resultat: number;
    gbAndel: number;
    budKost: number;
    kostAkk: number;
    kostKvartal: number;
    salg: {
      totalEnheter: number;
      solgtAkk: number;
      solgtKvartal: number;
      verdiKvartal: number;
      verdiAkk: number;
    };
    fremdrift: {
      milepaelerNaddKvartal: number;
      milepaelerForsinket: number;
      aktiviteterFullfortKvartal: number;
    };
  };
  projects: ReportProject[];
};

export type ProjectKpi = {
  quarter: { label: string; year: number; q: number };
  project: ReportProject;
};

export async function fetchProjectKpi(
  projectId: number,
  year: number,
  q: number
): Promise<ProjectKpi> {
  const res = await fetch(
    `/api/projects/${projectId}/report-kpi?year=${year}&q=${q}`
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ProjectKpi;
}

export type Narrative = { source: "ai" | "auto"; text: string };

export type ExplanationSection = { heading: string; body: string };
export type Explanation = {
  quarter: { label: string; year: number; q: number };
  explanation: { source: "ai" | "auto"; sections: ExplanationSection[] };
};

export async function fetchExplanation(
  projectId: number,
  year: number,
  q: number
): Promise<Explanation> {
  const res = await fetch(
    `/api/projects/${projectId}/report-explain?year=${year}&q=${q}`
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as Explanation;
}

// ---- Kvartalsrapport: klientcache + forhåndshenting (Erik 31.10.2026: «tar altfor
// lang tid å komme seg inn») ----
// API-et bruker ~11 s uten varm cache (docs/ytelse-okonomi.md). Svaret huskes i
// minnet og i sessionStorage (forsvinner når fanen lukkes; økonomitall blir ikke
// liggende), vises med en gang ved neste besøk og oppdateres stille i bakgrunnen.
// Samme kall som allerede pågår (f.eks. forhåndshentingen) gjenbrukes.
const KVARTAL_FERSK_MS = 10 * 60_000;
const LAGRING = "gb-kvartalsrapport:";
const kvartalMinne = new Map<string, { tid: number; svar: ReportData }>();
const pagaende = new Map<string, Promise<ReportData>>();
const kvartalNokkel = (year: number, q: number) => `${year}:${q}`;

/** Sist hentede svar for kvartalet (uansett alder), eller null. */
export function cachetKvartal(year: number, q: number): { tid: number; svar: ReportData } | null {
  const k = kvartalNokkel(year, q);
  const i = kvartalMinne.get(k);
  if (i) return i;
  try {
    const lagret = sessionStorage.getItem(LAGRING + k);
    if (lagret) {
      const v = JSON.parse(lagret) as { tid: number; svar: ReportData };
      kvartalMinne.set(k, v);
      return v;
    }
  } catch {
    // lagring er bare en bekvemmelighet
  }
  return null;
}

export function fetchQuarterly(year: number, q: number): Promise<ReportData> {
  const k = kvartalNokkel(year, q);
  const igang = pagaende.get(k);
  if (igang) return igang;
  const p = (async () => {
    const res = await fetch(`/api/reports/quarterly?year=${year}&q=${q}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const svar = (await res.json()) as ReportData;
    const v = { tid: Date.now(), svar };
    kvartalMinne.set(k, v);
    try {
      sessionStorage.setItem(LAGRING + k, JSON.stringify(v));
    } catch {
      // full eller blokkert lagring: minnet holder
    }
    return svar;
  })().finally(() => pagaende.delete(k));
  pagaende.set(k, p);
  return p;
}

/** Er det lagrede svaret ferskt nok til å slippe ny henting? */
export function kvartalErFersk(year: number, q: number): boolean {
  const c = cachetKvartal(year, q);
  return !!c && Date.now() - c.tid < KVARTAL_FERSK_MS;
}

/** Forrige avsluttede kvartal – standardvalget i kvartalsrapporten. */
export function forrigeKvartal(now = new Date()): { year: number; q: number } {
  const { year, q } = currentQuarter(now);
  return kvartalFor(year, q);
}

/** Kvartalet før (år, kvartal). */
export const kvartalFor = (year: number, q: number) => (q === 1 ? { year: year - 1, q: 4 } : { year, q: q - 1 });

/** Henter ett kvartal i bakgrunnen hvis det ikke allerede er ferskt. Stille ved feil. */
export async function forhandsHentKvartal(year: number, q: number): Promise<void> {
  if (kvartalErFersk(year, q)) return;
  await fetchQuarterly(year, q).catch(() => {
    // stille: siden henter selv ved besøk
  });
}

/**
 * Kalles etter innlogging: de to siste avsluttede kvartalene, ett om gangen
 * (Erik valgte Q2 etter at Q3 var klar, 31.10.2026), så siden er klar.
 */
export async function forhandsHentKvartalsrapport(): Promise<void> {
  const forste = forrigeKvartal();
  await forhandsHentKvartal(forste.year, forste.q);
  const andre = kvartalFor(forste.year, forste.q);
  await forhandsHentKvartal(andre.year, andre.q);
}

export async function fetchNarrative(
  level: "portfolio" | "project",
  quarterLabel: string,
  data: unknown
): Promise<Narrative> {
  const res = await fetch(`/api/reports/narrative`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ level, quarterLabel, data }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as Narrative;
}

/** Nåværende/siste avsluttede kvartal relativt til en dato. */
export function currentQuarter(now = new Date()): { year: number; q: number } {
  const y = now.getFullYear();
  const q = Math.floor(now.getMonth() / 3) + 1;
  return { year: y, q };
}
